import { useEffect } from "react";

// Canonical production domain (used for canonical URLs + absolute schema URLs).
export const SITE_URL = "https://equinoxsystems.tech";
const DEFAULT_IMAGE = `${SITE_URL}/equinox-og.jpg`;

function upsertMeta(selector, attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Dependency-free SEO head manager (React 19 safe). Sets the document title,
 * meta description, Open Graph / Twitter tags, canonical URL, and injects any
 * page-level JSON-LD schema objects. Designed to be swapped for Next.js
 * metadata exports during a future SSR migration with minimal changes.
 */
export default function Seo({ title, description, path = "/", image, schemas = [] }) {
  const schemaKey = JSON.stringify(schemas);
  useEffect(() => {
    const url = `${SITE_URL}${path}`;
    const img = image || DEFAULT_IMAGE;

    if (title) document.title = title;
    if (description) upsertMeta('meta[name="description"]', "name", "description", description);
    upsertMeta('meta[property="og:title"]', "property", "og:title", title || document.title);
    if (description) upsertMeta('meta[property="og:description"]', "property", "og:description", description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", url);
    upsertMeta('meta[property="og:image"]', "property", "og:image", img);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", title || document.title);
    if (description) upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    upsertMeta('meta[name="twitter:image"]', "name", "twitter:image", img);
    upsertCanonical(url);

    const nodes = [];
    (schemas || []).filter(Boolean).forEach((schema) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.text = JSON.stringify(schema);
      document.head.appendChild(s);
      nodes.push(s);
    });
    return () => nodes.forEach((n) => n.remove());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, schemaKey]);

  return null;
}
